var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { BigNumber } from "ethers";
import colors from "shared/lib/designSystem/colors";
import { PrimaryText, SecondaryText, Title } from "shared/lib/designSystem";
import { getAssetDisplay } from "shared/lib/utils/asset";
import { formatBigNumber, isPracticallyZero } from "shared/lib/utils/math";
import { MigrateIcon } from "shared/lib/assets/icons/icons";
import { getVaultColor } from "shared/lib/utils/vault";
import { ActionButton } from "shared/lib/components/Common/buttons";
import useVaultActionForm from "../../../../hooks/useVaultActionForm";
import { ACTIONS } from "../Modal/types";
import { useVaultData, useV2VaultData } from "shared/lib/hooks/web3DataContext";
import TooltipExplanation from "shared/lib/components/Common/TooltipExplanation";
import HelpInfo from "shared/lib/components/Common/HelpInfo";
import useVaultAccounts from "shared/lib/hooks/useVaultAccounts";
var MigrateLogoContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 64px;\n  height: 64px;\n  border-radius: 100px;\n  background: ", "14;\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 64px;\n  height: 64px;\n  border-radius: 100px;\n  background: ", "14;\n"])), function (props) { return props.color; });
var FormTitle = styled(Title)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  font-size: 22px;\n  line-height: 28px;\n  letter-spacing: 1px;\n"], ["\n  font-size: 22px;\n  line-height: 28px;\n  letter-spacing: 1px;\n"])));
var FormDescription = styled(PrimaryText)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  font-size: 14px;\n  line-height: 20px;\n  color: ", ";\n"], ["\n  font-size: 14px;\n  line-height: 20px;\n  color: ", ";\n"])), colors.text);
var FormColumnData = styled(Title)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  font-size: 14px;\n  line-height: 20px;\n"], ["\n  font-size: 14px;\n  line-height: 20px;\n"])));
var VaultV2MigrationForm = function (_a) {
    var vaultOption = _a.vaultOption, migrateSourceVault = _a.migrateSourceVault, onFormSubmit = _a.onFormSubmit, onHideForm = _a.onHideForm;
    var _b = useVaultData(migrateSourceVault), vaultMaxWithdrawAmount = _b.vaultMaxWithdrawAmount, asset = _b.asset, decimals = _b.decimals;
    var _c = useV2VaultData(vaultOption).data, totalBalance = _c.totalBalance, cap = _c.cap;
    var color = getVaultColor(vaultOption);
    var _d = useVaultActionForm(vaultOption), handleActionTypeChange = _d.handleActionTypeChange, handleMaxClick = _d.handleMaxClick;
    var v1VaultAccounts = useVaultAccounts("v1").vaultAccounts;
    var vaultAccount = v1VaultAccounts[migrateSourceVault];
    var migrationLimit = useMemo(function () {
        var v2Capacity = cap.sub(totalBalance);
        var normalizedV2capacity = v2Capacity.isNegative()
            ? BigNumber.from(0)
            : v2Capacity;
        return vaultMaxWithdrawAmount.gte(normalizedV2capacity)
            ? normalizedV2capacity
            : vaultMaxWithdrawAmount;
    }, [cap, totalBalance, vaultMaxWithdrawAmount]);
    var error = useMemo(function () {
        if (!isPracticallyZero(vaultAccount.totalStakedBalance, decimals)) {
            return "amountStaked";
        }
        if (vaultAccount.totalBalance.gt(migrationLimit)) {
            return "insufficientMigrationLimit";
        }
        return undefined;
    }, [
        decimals,
        migrationLimit,
        vaultAccount.totalBalance,
        vaultAccount.totalStakedBalance,
    ]);
    /**
     * Show migration form here
     */
    var handleMigrate = useCallback(function () {
        handleActionTypeChange(ACTIONS.migrate, "v1", { migrateSourceVault: migrateSourceVault });
        handleMaxClick();
        onFormSubmit();
    }, [
        handleActionTypeChange,
        handleMaxClick,
        migrateSourceVault,
        onFormSubmit,
    ]);
    var errorText = useMemo(function () {
        switch (error) {
            case "amountStaked":
                return (_jsx(SecondaryText, __assign({ className: "mt-3 text-center", color: colors.red }, { children: "Before you migrate to V2 you must unstake your funds from the rETH-THETA staking pool" }), void 0));
            case "insufficientMigrationLimit":
                return (_jsxs(SecondaryText, __assign({ className: "mt-3 text-center", color: colors.red }, { children: ["Migrating your ", getAssetDisplay(asset), " from V1 to V2 will breach the weekly migration limit. Please try again on Friday at 12pm UTC when the weekly migration limit is reset."] }), void 0));
        }
        return _jsx(_Fragment, {}, void 0);
    }, [error, asset]);
    return (_jsxs("div", __assign({ className: "d-flex flex-column align-items-center p-4" }, { children: [_jsx(MigrateLogoContainer, __assign({ color: color, className: "mt-3" }, { children: _jsx(MigrateIcon, { color: color, height: 27 }, void 0) }), void 0), _jsx(FormTitle, __assign({ className: "mt-3" }, { children: "MIGRATE TO V2" }), void 0), _jsxs(FormDescription, __assign({ className: "mt-3 text-center" }, { children: [getAssetDisplay(asset), " deposits can now be migrated over from the V1 vault"] }), void 0), _jsxs("div", __assign({ className: "d-flex w-100 align-items-center mt-4" }, { children: [_jsx(SecondaryText, { children: "Your V1 Balance" }, void 0), _jsxs(FormColumnData, __assign({ className: "ml-auto" }, { children: [formatBigNumber(vaultAccount.totalBalance, decimals), " ", getAssetDisplay(asset)] }), void 0)] }), void 0), _jsxs("div", __assign({ className: "d-flex w-100 align-items-center mt-4" }, { children: [_jsx(SecondaryText, { children: "Migration Limit" }, void 0), _jsx(TooltipExplanation, { title: "MIGRATION LIMIT", explanation: "The weekly migration limit is the max amount that can be migrated to the v2 vault. This amount takes into account the v1 vault's weekly withdrawal limit and the v2 vault's initial capacity.", renderContent: function (_a) {
                            var ref = _a.ref, triggerHandler = __rest(_a, ["ref"]);
                            return (_jsx(HelpInfo, __assign({ containerRef: ref }, triggerHandler, { children: "i" }), void 0));
                        } }, void 0), _jsxs(FormColumnData, __assign({ className: "ml-auto", color: error === "insufficientMigrationLimit" ? colors.red : undefined }, { children: [formatBigNumber(migrationLimit, decimals), " ", getAssetDisplay(asset)] }), void 0)] }), void 0), _jsxs(ActionButton, __assign({ color: color, className: "py-3 mt-4", disabled: Boolean(error), onClick: handleMigrate }, { children: ["Migrate ", getAssetDisplay(asset)] }), void 0), errorText, _jsx(SecondaryText, __assign({ className: "mt-4 ", color: colors.primaryText, role: "button", onClick: onHideForm }, { children: _jsx("u", { children: "Skip Migration" }, void 0) }), void 0)] }), void 0));
};
export default VaultV2MigrationForm;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=VaultV2MigrationForm.js.map